import {
  FileUploader,
  LoadingIndicator,
  MUICheckBox,
  MUISelect,
  MUITextField,
} from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useFileTypes } from "../../../../hooks/useFileTypes"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import { FilePost } from "../../../models/File"
import { ControllerRendererProps } from "../../../types/reactHookForm"

type FilePostFields = "name" | "suffix" | "extension" | "description"

const validationError =
  "Only letters, numbers, underscores (_) and hyphens (-) are allowed"
// Arcadia completes its internal upload animation in ten 500 ms steps.
const FILE_UPLOADER_PROCESSING_MS = 5100

const validationSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .regex(/^[a-zA-Z0-9_-]+$/, {
      message: validationError,
    }),
  suffix: z.string().regex(/^[a-zA-Z0-9_-]*$/, {
    message: validationError,
  }),
  extension: z
    .string()
    .min(1, "Extension is required")
    .regex(/^[a-zA-Z0-9_-]+$/, {
      message: validationError,
    }),
  description: z.string(),
  typeId: z.string().nullable(),
  isPublic: z.boolean({ error: "Is Public is required" }),
  quickDownload: z.boolean({ error: "Quick download is required" }),
  file: z.any().optional(),
})

export function FileCreate() {
  const navigate = useNavigate()
  const returnURL = `/files`
  const { fileTypes, isLoadingFileTypes } = useFileTypes()
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [isUploadClicked, setIsUploadClicked] = useState(false)
  const [isFileReady, setIsFileReady] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const fileReadyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveRequestInFlightRef = useRef(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<FilePost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      name: "",
      suffix: "",
      extension: "",
      description: "",
      typeId: null,
      isPublic: false,
      quickDownload: false,
      file: null,
    })
  }, [reset])

  useEffect(
    () => () => {
      if (fileReadyTimeoutRef.current) {
        clearTimeout(fileReadyTimeoutRef.current)
      }
    },
    []
  )

  const onSubmit = async (values: FilePost) => {
    const formData = new FormData()
    formData.append("name", values.name)
    formData.append("suffix", values.suffix)
    formData.append("extension", values.extension)
    formData.append("description", values.description)
    formData.append("typeId", values.typeId ? values.typeId : "")
    formData.append("isPublic", String(values.isPublic))
    formData.append("quickDownload", String(values.quickDownload))
    if (values.file instanceof File) {
      formData.append("formFile", values.file)
    } else {
      toast.error("File is required")
      return
    }

    if (saveRequestInFlightRef.current) {
      return
    }

    saveRequestInFlightRef.current = true
    setIsSaving(true)
    try {
      const response = await digitalManagerApi.post(`/files`, formData, {
        headers: { "Content-type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && progressEvent.total > 0) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            )
            setUploadProgress(progress)
          } else {
            setUploadProgress(0)
          }
        },
      })

      if (response.status === StatusCodes.CREATED) {
        toast.success("File created")
        navigate(returnURL)
      }
    } catch {
      toast.error("Cannot create file")
    } finally {
      setUploadProgress(0)
      setIsSaving(false)
      saveRequestInFlightRef.current = false
    }
  }

  const submitForm = handleSubmit(async (values) => {
    setIsUploadClicked(true)
    setUploadProgress(0)
    await onSubmit(values)
  })

  const selectedFile = useWatch({
    control,
    name: "file",
  })

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<FilePost, FilePostFields>) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: ControllerRendererProps<FilePost, "name">) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const renderSuffixTextField = useCallback(
    ({ field }: ControllerRendererProps<FilePost, "suffix">) => (
      <MUITextField
        id="suffix"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.suffix?.message === "string"
            ? { suffix: { message: errors.suffix.message } }
            : undefined
        }
      />
    ),
    [errors.suffix, handleTextFieldChange]
  )

  const renderExtensionTextField = useCallback(
    ({ field }: ControllerRendererProps<FilePost, "extension">) => (
      <MUITextField
        id="extension"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.extension?.message === "string"
            ? { extension: { message: errors.extension.message } }
            : undefined
        }
      />
    ),
    [errors.extension, handleTextFieldChange]
  )

  const renderDescriptionTextField = useCallback(
    ({ field }: ControllerRendererProps<FilePost, "description">) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleTextFieldChange]
  )

  const handleTypeSelectChange = useCallback(
    (field: ControllerRenderProps<FilePost, "typeId">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderTypeSelect = useCallback(
    ({ field }: ControllerRendererProps<FilePost, "typeId">) => (
      <MUISelect
        name={field.name}
        id="typeId"
        size={32}
        dropdownLOV={[
          { id: "", name: "" },
          ...fileTypes.map((f) => ({ id: String(f.id), name: f.name ?? "" })),
        ]}
        selectedItem={[field.value ? String(field.value) : ""]}
        onChange={handleTypeSelectChange(field)}
        onBlur={field.onBlur}
        isDisabled={isLoadingFileTypes}
      />
    ),
    [isLoadingFileTypes, fileTypes, handleTypeSelectChange]
  )

  const handleToggle = useCallback(
    (field: ControllerRenderProps<FilePost, "isPublic" | "quickDownload">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckboxIsPublic = useCallback(
    ({ field }: ControllerRendererProps<FilePost, "isPublic">) => (
      <MUICheckBox
        id={field.name}
        checked={!!field.value}
        handleToggle={handleToggle(field)}
      />
    ),
    [handleToggle]
  )

  const renderCheckboxQuickDownload = useCallback(
    ({ field }: ControllerRendererProps<FilePost, "quickDownload">) => (
      <MUICheckBox
        id={field.name}
        checked={!!field.value}
        handleToggle={handleToggle(field)}
      />
    ),
    [handleToggle]
  )

  const handleFileUpload = useCallback(
    (files: File[]) => {
      if (fileReadyTimeoutRef.current) {
        clearTimeout(fileReadyTimeoutRef.current)
      }

      setIsFileReady(false)
      setValue("file", files[0] ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      })

      if (files[0]) {
        fileReadyTimeoutRef.current = setTimeout(() => {
          setIsFileReady(true)
          fileReadyTimeoutRef.current = null
        }, FILE_UPLOADER_PROCESSING_MS)
      }
    },
    [setValue]
  )

  const handleFileDelete = useCallback(() => {
    if (fileReadyTimeoutRef.current) {
      clearTimeout(fileReadyTimeoutRef.current)
      fileReadyTimeoutRef.current = null
    }
    setIsFileReady(false)
    setValue("file", null, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }, [setValue])

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isSubmitting || isSaving}
      isDirty={isDirty && !!selectedFile}
      isValid={isValid && !!selectedFile && isFileReady}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Suffix">
        <Controller name="suffix" control={control} render={renderSuffixTextField} />
      </FieldRow>
      <FieldRow label="Extension">
        <Controller
          name="extension"
          control={control}
          render={renderExtensionTextField}
        />
      </FieldRow>
      <FieldRow label="Description">
        <Controller
          name="description"
          control={control}
          render={renderDescriptionTextField}
        />
      </FieldRow>
      <FieldRow label="Type">
        <Controller name="typeId" control={control} render={renderTypeSelect} />
      </FieldRow>
      <FieldRow label="Is Public">
        <Controller name="isPublic" control={control} render={renderCheckboxIsPublic} />
      </FieldRow>
      <FieldRow label="Quick Download">
        <Controller
          name="quickDownload"
          control={control}
          render={renderCheckboxQuickDownload}
        />
      </FieldRow>
      <FieldRow label="File">
        <FileUploader
          variant="single"
          id="file"
          disabled={isSubmitting || isSaving}
          onFileUpload={handleFileUpload}
          onFileDelete={handleFileDelete}
          errorMessages={{
            uploadSuccess: "File selected",
          }}
        />
        {errors.file && (
          <MUITextField
            isDisabled
            value=""
            errors={{
              file: {
                message:
                  typeof errors.file?.message === "string" ? errors.file.message : "",
              },
            }}
          />
        )}
      </FieldRow>
      {isUploadClicked && (
        <LoadingIndicator
          variant="progress-bar"
          size="medium"
          isDeterminate
          percentage={uploadProgress}
        />
      )}
    </FormPage>
  )
}
